import React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import CheckCircleRounded from '@mui/icons-material/CheckCircleRounded';
import ErrorRounded from '@mui/icons-material/ErrorRounded';
import { useNavigate } from 'react-router-dom';
import {
  EmptyState, ListTable, MetricCard, Modal, Panel, StatusChip, ToolbarButton, type ListColumn,
} from '../../../ui-kit';
import {
  cwRunAction, fetchCwRun, fetchCwShot, tryToBuy, type CwRunDetail, type CwStep,
} from '../../../services/carrierWebsiteService';
import { useLive } from '../rocco/live';
import { useAgentAddress } from './address';
import { money, runChip } from './status';

const HOW: Record<string, string> = { guide: 'From the guide', ai: 'Worked out by AI', code: 'Klaris', person: 'A person' };

function StepRow({ step }: { step: CwStep }) {
  const refused = step.kind === 'refused';
  const bad = refused || step.ok === false;
  return (
    <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ py: 0.75 }}>
      {bad
        ? <ErrorRounded sx={(t) => ({ fontSize: 18, color: t.tokens.semantic.danger })} />
        : <CheckCircleRounded sx={(t) => ({
          fontSize: 18,
          // Worked out by the AI rather than the guide: the ones worth a look.
          color: step.how === 'ai' ? t.tokens.semantic.warning : step.ok ? t.tokens.semantic.success : t.tokens.text.tertiary,
        })} />}
      <Stack sx={{ minWidth: 0, flex: 1 }}>
        <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.primary, overflowWrap: 'anywhere' })}>
          {refused ? `Refused: ${step.label ?? ''}` : step.label ?? step.note}
          {step.kind === 'click' ? ' pressed' : ''}
          {step.value ? ` · ${step.value}` : ''}
        </Typography>
        <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.tertiary, overflowWrap: 'anywhere' })}>
          {[step.source, step.how ? HOW[step.how] : null, step.label ? step.note : null].filter(Boolean).join(' · ')}
        </Typography>
      </Stack>
    </Stack>
  );
}

function Shot({ id, screen }: { id: string; screen: string | null }) {
  const [url, setUrl] = React.useState<string | null>(null);
  const [open, setOpen] = React.useState(false);
  React.useEffect(() => {
    let made: string | null = null;
    fetchCwShot(id).then((u) => { made = u; setUrl(u); }).catch(() => undefined);
    return () => { if (made) URL.revokeObjectURL(made); };
  }, [id]);
  return (
    <>
      <Box component="button" onClick={() => setOpen(true)} aria-label={`Open screenshot of ${screen ?? 'this screen'}`}
        sx={(t) => ({ p: 0, border: `1px solid ${t.tokens.border.subtle}`, borderRadius: `${t.ds.radius.sm}px`, overflow: 'hidden', cursor: 'pointer', bgcolor: t.tokens.surface.raised })}>
        {url ? <Box component="img" src={url} alt={screen ?? ''} sx={{ width: '100%', display: 'block' }} /> : <Box sx={{ height: 90 }} />}
      </Box>
      <Modal open={open} onClose={() => setOpen(false)} title={screen ?? 'Screenshot'}>
        {url ? <Box component="img" src={url} alt={screen ?? ''} sx={{ width: '100%', display: 'block' }} /> : null}
      </Modal>
    </>
  );
}

interface ScreenRow { screen: string; filled: number; matched: number; problems: number; ai: boolean; person: boolean }

const screenColumns: ListColumn<ScreenRow>[] = [
  {
    key: 'screen', header: 'Screen', grow: 1, minWidth: 200,
    render: (r) => (
      <Stack direction="row" spacing={1} alignItems="center">
        {r.problems
          ? <ErrorRounded sx={(t) => ({ fontSize: 18, color: t.tokens.semantic.danger })} />
          : <CheckCircleRounded sx={(t) => ({ fontSize: 18, color: r.filled ? t.tokens.semantic.success : t.tokens.text.tertiary })} />}
        <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.primary })}>{r.screen}</Typography>
      </Stack>
    ),
  },
  {
    key: 'fields', header: 'Fields filled', width: 190,
    render: (r) => (
      <Typography variant="body2" sx={(t) => ({ color: r.problems ? t.tokens.semantic.danger : t.tokens.text.secondary, fontVariantNumeric: 'tabular-nums' })}>
        {r.filled ? `${r.matched} of ${r.filled} matched` : 'Nothing to fill'}
      </Typography>
    ),
  },
  {
    key: 'how', header: 'Who', width: 200,
    render: (r) => (
      <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.secondary })}>
        {[r.ai ? 'AI helped' : 'Guide', r.person ? 'a person stepped in' : null].filter(Boolean).join(', ')}
      </Typography>
    ),
  },
];

/** One run: what it needs, what it did, and the proof. Updates by itself while it works. */
export default function RunPage({ runId, embedded = false }: { runId: string; embedded?: boolean }) {
  const fetcher = React.useCallback(() => fetchCwRun(runId), [runId]);
  const { data: run, error, loading } = useLive<CwRunDetail>(fetcher, 1500);
  const navigate = useNavigate();
  const base = useAgentAddress();
  const [notice, setNotice] = React.useState<string | null>(null);
  const [shown, setShown] = React.useState<string | null>(null);   // a screen, or 'all'

  if (error && !run) return <EmptyState message={error} />;
  const open = run ? ['ready', 'working', 'waiting', 'paused', 'stuck'].includes(run.status) : false;
  const waiting = (run?.questions ?? []).filter((q) => q.open);
  const steps = run?.steps ?? [];
  const screens = Array.from(new Set(steps.map((s) => s.screen ?? 'Starting')));
  const screenRows: ScreenRow[] = [...screens].reverse().map((screen) => {
    const here = steps.filter((x) => (x.screen ?? 'Starting') === screen);
    const fills = here.filter((x) => x.kind === 'fill');
    return {
      screen,
      filled: fills.length,
      matched: fills.filter((x) => x.ok).length,
      problems: here.filter((x) => x.kind === 'refused' || x.ok === false).length,
      ai: here.some((x) => x.how === 'ai'),
      person: here.some((x) => x.how === 'person'),
    };
  });
  const shotsHere = (run?.shots ?? []).filter((x) => shown === 'all' || (x.screen ?? 'Starting') === shown);

  const buy = async () => {
    const reply = await tryToBuy(runId);
    setNotice(reply.refused ? `Refused. ${reply.why}.` : reply.why);
  };

  return (
    <Stack spacing={2} sx={{ pb: embedded ? 0 : 4 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2} sx={{ flexWrap: 'wrap', gap: 1 }}>
        <Stack spacing={0.25}>
          <Stack direction="row" alignItems="center" spacing={1}>
            {/* In a window, the window's title already names the customer. */}
            {embedded ? null : <Typography variant="h4" sx={(t) => ({ color: t.tokens.text.primary })}>{run?.customer ?? 'Run'}</Typography>}
            {run ? <StatusChip {...runChip(run)} /> : null}
          </Stack>
          <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.secondary })}>
            {run ? [run.carrier_name, run.mode === 'learn' ? 'First visit' : 'Followed the guide',
              run.review === 'every' ? 'Stops on every screen' : 'Stops when unsure', run.screen].filter(Boolean).join(' · ') : ' '}
          </Typography>
        </Stack>
        <Stack direction="row" spacing={1}>
          {embedded ? null : <ToolbarButton variant="ghost" onClick={() => navigate(`${base}/runs`)}>All runs</ToolbarButton>}
          {run && run.status === 'stopped' ? (
            <ToolbarButton variant="ghost" onClick={buy}>Tell the agent to buy</ToolbarButton>
          ) : null}
          {run && run.status === 'stopped' ? (
            <ToolbarButton variant="brand" tooltip="Once you have bound it on the carrier's website yourself"
              onClick={() => cwRunAction(runId, 'bound')}>Mark as bound</ToolbarButton>
          ) : null}
          {run && (run.status === 'working' || run.status === 'waiting')
            ? <ToolbarButton variant="ghost" onClick={() => cwRunAction(runId, 'pause')}>Take over</ToolbarButton> : null}
          {run && (run.status === 'paused' || run.status === 'stuck')
            ? <ToolbarButton variant="brand" onClick={() => cwRunAction(runId, 'resume')}>Give it back</ToolbarButton> : null}
          {open ? <ToolbarButton variant="ghost" onClick={() => cwRunAction(runId, 'end')}>End</ToolbarButton> : null}
        </Stack>
      </Stack>

      {notice ? <Typography variant="body2" sx={(t) => ({ color: t.tokens.semantic.danger })}>{notice}</Typography> : null}

      <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' } }}>
        <MetricCard loading={loading} label={run?.price_kind === 'indication' ? 'Rough price' : 'Firm price'} value={money(run?.price ?? null, run?.currency)}
          info="As shown on the carrier's website when the agent stopped." />
        <MetricCard loading={loading} label="Screens finished" value={String(run?.screens_done ?? 0)} />
        <MetricCard loading={loading} label="Accuracy"
          value={`${steps.filter((s) => s.kind === 'fill' && s.ok).length} of ${steps.filter((s) => s.kind === 'fill').length}`}
          info="Fields where what the page shows matched what we meant to enter." />
        <MetricCard loading={loading} label="Times AI was needed" value={String(run?.ai_calls ?? 0)}
          info="A first visit needs it often. A website we know should need it rarely." />
      </Box>

      {run?.reason || waiting.length ? (
        <Panel title={waiting.length ? 'Waiting for you' : run?.status === 'stopped' ? 'Stopped' : 'Why it paused'}
          subtitle={waiting.some((q) => q.kind === 'review' || q.kind === 'allow')
            ? 'Check what we entered. Values we worked out, or that the page did not take, are marked'
            : waiting.length ? "Fill this in on the carrier's page, and we will carry on" : undefined}
          compact
          actions={waiting.some((q) => q.kind === 'review' || q.kind === 'allow')
            ? <ToolbarButton variant="brand" onClick={() => setShown('all')}>Check everything</ToolbarButton> : null}>
          <Stack spacing={0.5}>
            {run?.reason ? (
              <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.primary })}>
                {run.reason}{run.status === 'stopped' ? '. Nothing has been bought. A licensed person takes it from here.' : ''}
              </Typography>
            ) : null}
            {waiting.map((q) => (
              <Typography key={q.id} variant="subtitle2" sx={(t) => ({ color: t.tokens.text.primary })}>{q.text}</Typography>
            ))}
          </Stack>
        </Panel>
      ) : null}

      <Panel compact title="Screens" subtitle="One row a screen. Open one to see every value, where it came from, and whether the page agreed"
        actions={steps.length ? <ToolbarButton variant="ghost" onClick={() => setShown('all')}>Every step</ToolbarButton> : null}>
        <ListTable columns={screenColumns} rows={screenRows} getRowKey={(r) => r.screen} loading={loading}
          onRowClick={(r) => setShown(r.screen)} emptyMessage="Nothing yet. The website is opening." />
      </Panel>

      <Modal open={!!shown} onClose={() => setShown(null)} size="lg"
        title={shown === 'all' ? 'Every step' : shown ?? ''}
        subtitle={run ? [run.customer, run.carrier_name].filter(Boolean).join(' · ') : undefined}>
        <Stack spacing={2.5}>
          {(shown === 'all' ? [...screens].reverse() : screens.filter((x) => x === shown)).map((screen) => (
            <Box key={screen}>
              {shown === 'all' ? (
                <Typography variant="subtitle2" sx={(t) => ({ color: t.tokens.text.secondary })}>{screen}</Typography>
              ) : null}
              {steps.filter((x) => (x.screen ?? 'Starting') === screen).map((x) => <StepRow key={x.id} step={x} />)}
            </Box>
          ))}
          {shown === 'all' && run?.questions.some((q) => !q.open) ? (
            <Box>
              <Typography variant="subtitle2" sx={(t) => ({ color: t.tokens.text.secondary })}>What people did on the page</Typography>
              {run.questions.filter((q) => !q.open).map((q) => (
                <Stack key={q.id} sx={{ py: 0.75 }}>
                  <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.primary })}>{q.text}</Typography>
                  <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.tertiary })}>{[q.answer, q.by].filter(Boolean).join(' · ')}</Typography>
                </Stack>
              ))}
            </Box>
          ) : null}
          {shotsHere.length ? (
            <Box>
              <Typography variant="subtitle2" sx={(t) => ({ color: t.tokens.text.secondary, mb: 1 })}>Screenshots</Typography>
              <Box sx={{ display: 'grid', gap: 1, gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))' }}>
                {shotsHere.map((shot) => <Shot key={shot.id} id={shot.id} screen={shot.screen} />)}
              </Box>
            </Box>
          ) : null}
        </Stack>
      </Modal>
    </Stack>
  );
}
